import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { marketplace, ThirdPartyIntegrationDetail } from '../../../services/api';
import { useTranslation } from '../../../contexts/TranslationContext';
import './IntegrationModal.css';

interface IntegrationModalProps {
  integrationId: number;
  isOpen: boolean;
  onClose: () => void;
  onAdd: (integrationId: number) => void;
}

const IntegrationModal: React.FC<IntegrationModalProps> = ({
  integrationId,
  isOpen,
  onClose,
  onAdd,
}) => {
  const { t, currentLanguage } = useTranslation();
  const [integration, setIntegration] = useState<ThirdPartyIntegrationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && integrationId) {
      fetchIntegrationDetails();
    }
  }, [isOpen, integrationId]);

  const fetchIntegrationDetails = async () => {
    try {
      setLoading(true);
      const response = await marketplace.getIntegrationDetail(integrationId);
      setIntegration(response.data);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch integration details:', error);
      setError(t('marketplace.failedToLoadDetails'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    if (integration) {
      onAdd(integration.id);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderConfigurationSchema = (schema: any) => {
    if (!schema) return null;

    const { required_fields, optional_fields } = schema;

    return (
      <div className="configuration-preview">
        <h4>{t('marketplace.configurationRequirements')}</h4>
        
        {required_fields && Object.keys(required_fields).length > 0 && (
          <div className="config-section">
            <h5>{t('marketplace.requiredFields')}</h5>
            <ul>
              {Object.entries(required_fields).map(([key, field]: [string, any]) => (
                <li key={key}>
                  <strong>{field.label}</strong> - {field.description}
                  {field.sensitive && <span className="sensitive-badge"> {t('marketplace.sensitiveField')}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {optional_fields && Object.keys(optional_fields).length > 0 && (
          <div className="config-section">
            <h5>{t('marketplace.optionalFields')}</h5>
            <ul>
              {Object.entries(optional_fields).map(([key, field]: [string, any]) => (
                <li key={key}>
                  <strong>{field.label}</strong> - {field.description}
                  {field.default && <span className="default-value">{t('marketplace.defaultValue')}: {field.default}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="integration-modal">
        <div className="modal-header">
          <button className="close-button" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>{t('marketplace.loadingDetails')}</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={fetchIntegrationDetails} className="retry-button">
                {t('marketplace.tryAgain')}
              </button>
            </div>
          ) : integration ? (
            <>
              <div className="integration-header">
                {integration.image_url ? (
                  <img
                    src={integration.image_url}
                    alt={integration.title}
                    className="integration-logo-large"
                  />
                ) : (
                  <div className="integration-logo-placeholder-large">
                    <span>{integration.title.charAt(0).toUpperCase()}</span>
                  </div>
                )}
                
                <div className="integration-info">
                  <h2>{integration.title}</h2>
                  <div className="brief-description">
                    <ReactMarkdown>{integration.brief_description}</ReactMarkdown>
                  </div>
                  
                  <div className="pricing-info">
                    <span className="price">
                      {integration.monthly_price} {currentLanguage === 'fa' ? 'تومان' : 'IRR'}
                    </span>
                    <span className="period">{t('marketplace.monthlyPrice')}</span>
                  </div>
                </div>
              </div>

              <div className="integration-details">
                {integration.detailed_description && (
                  <div className="description-section">
                    <h3>{t('marketplace.descriptionTitle')}</h3>
                    <div 
                      className="detailed-description"
                    >
                      <ReactMarkdown>{integration.detailed_description}</ReactMarkdown>
                    </div>
                  </div>
                )}

                {integration.configuration_schema && (
                  renderConfigurationSchema(integration.configuration_schema)
                )}

                {integration.terms_and_conditions && (
                  <div className="terms-section">
                    <h3>{t('marketplace.termsAndConditions')}</h3>
                    <div className="terms-content">
                      <ReactMarkdown>{integration.terms_and_conditions}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                {integration.third_party_link && (
                  <a
                    href={integration.third_party_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                  >
                    {t('marketplace.visitWebsite')}
                  </a>
                )}
                
                <button
                  className="btn-primary"
                  onClick={handleAddClick}
                  disabled={!integration.is_active}
                >
                  {t('marketplace.addToAida')}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default IntegrationModal; 